/* eslint-disable @typescript-eslint/naming-convention */
import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { i18nFilter } from "app/filters/i18nFilter";
import { countryTextByIdFilter } from "app/filters/ngFilters";
import _ from "lodash";
import dayjs from "dayjs";
import { allTrackers } from "app/services/track/track";
import { getSiteDataFromResponse } from "app/services/sitesService/sitesUtils";

import InvestorsWorkspaceApiService from "../../../../services/workspaces/investorsWorkspaceApiService";
import SalesWorkspaceApiService from "../../../../services/workspaces/salesWorkspaceApiService";
import {
    COMMON_WORKSPACE_LIST_SEARCH,
    COMMON_WORKSPACE_LIST_SORT,
    COMMON_WORKSPACES_ADD_OPPORTUNITIES_FAIL,
    COMMON_WORKSPACES_ADD_OPPORTUNITIES_START,
    COMMON_WORKSPACES_ADD_OPPORTUNITIES_SUCCESS,
    COMMON_WORKSPACES_ADD_OPPORTUNITY_LIST,
    COMMON_WORKSPACES_ADD_WORKSPACE,
    COMMON_WORKSPACES_DELETE_OPPORTUNITY_LIST,
    COMMON_WORKSPACES_FETCH_ENRICHED_DATA,
    COMMON_WORKSPACES_FETCH_LIST_DATA_START,
    COMMON_WORKSPACES_FETCH_RANKS_DATA,
    COMMON_WORKSPACES_FETCH_RECOMMENDATIONS,
    COMMON_WORKSPACES_FETCH_WORKSPACES,
    COMMON_WORKSPACES_FETCH_WORKSPACES_FAILED,
    COMMON_WORKSPACES_FETCH_WORKSPACES_SUCCESS,
    COMMON_WORKSPACES_OPTIMISTIC_REMOVE_OPPORTUNITIES,
    COMMON_WORKSPACES_REMOVE_RECOMMENDATIONS,
    COMMON_WORKSPACES_RESET,
    COMMON_WORKSPACES_SELECT_ACTIVE_LIST,
    COMMON_WORKSPACES_SELECT_ACTIVE_WORKSPACE,
    COMMON_WORKSPACES_SELECT_DOMAIN,
    COMMON_WORKSPACES_SET_ACTIVE_LIST_DATA_SUCCESS,
    COMMON_WORKSPACES_SET_DESCRIPTION_DATA,
    COMMON_WORKSPACES_SET_ENRICHED_DATA,
    COMMON_WORKSPACES_SET_FEED_ITEM_FEEDBACK,
    COMMON_WORKSPACES_SET_RANKS_DATA,
    COMMON_WORKSPACES_SET_UNSUPPORTED_FEATURES,
    COMMON_WORKSPACES_TOGGLE_RECOMMENDATIONS,
    COMMON_WORKSPACES_TOGGLE_RIGHTBAR,
    COMMON_WORKSPACES_UPDATE_LIST_INFO,
    COMMON_WORKSPACES_UPDATE_LIST_SETTINGS,
    COMMON_WORKSPACES_UPDATE_RECOMMENDATIONS,
    COMMON_WORKSPACES_UPDATE_SNAPSHOT_DATE,
    WORKSPACE_SALES_MANIPULATION_EMAIL_DIGEST,
    WORKSPACE_SALES_MANIPULATION_WEBSITES_WIZARD,
    WORKSPACE_SALES_SELECT_ACTIVE_SAVED_SEARCH_LIST,
    WORKSPACE_SALES_UNSELECT_ACTIVE_SAVED_SEARCH_LIST,
    WORKSPACE_SALES_UPDATE_SAVED_SEARCH_LIST,
    COMMON_WORKSPACES_UPDATE_LIST_INFO_START,
} from "../action_types/actionTypes";
import { ANALYSIS_TAB, FEED_TAB, INVESTORS_TYPE, LIST_SETTING_FEED, OVERVIEW_ID } from "../consts";
import { addToWatchlistModal, editListModal } from "../modals_creators";
import type { ICommonWorkspaceState } from "../reducers/common_workspace_reducer";
import { selectActiveOpportunityList } from "../selectors";
import type { IOpportunityListItem } from "../types";
import { safeParse } from "../workspacesUtils";
import { isListActive } from "../workspaceUtilsIsListActive";
import {
    fetchSignalsForAllOrInverseThunkAction,
    fetchSignalsForCountryThunkAction,
    removeListIdFromSignalsUseThunkAction,
} from "app/pages/workspace/sales/sub-modules/signals/store/effects";
import {
    selectActiveSignalFilterId,
    selectActiveSignalsTab,
    selectActiveSignalSubFilterId,
} from "app/pages/workspace/sales/sub-modules/signals/store/selectors";
import {
    fetchAdNetworksThunkAction,
    fetchFeedsThunk,
    fetchTopCountriesThunk,
    fetchSiteInfoThunkAction,
} from "app/pages/workspace/sales/sub-modules/feed/store/effects";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";
import { toggleRightBar as toggleRightBarSales } from "../../sales/sub-modules/common/store/action-creators";
import {
    selectLeadListAction,
    selectWebsiteAction,
} from "app/pages/workspace/sales/sub-modules/opportunities-lists/store/action-creators";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import Sentry from "@similarweb/sentry-logger";

const commonActionCreators = ({ api, component }: FixmeType) => {
    let workspaceType = "";

    if (api instanceof SalesWorkspaceApiService) {
        workspaceType = "sales";
    }

    const selectActiveWorkspace = (workspaceId: string) => (dispatch: ThunkDispatchCommon) => {
        const { workspaceId: idInUrl } = proNavigator.getParams();

        if (idInUrl !== workspaceId) {
            proNavigator.updateParams({ workspaceId });
        }
        return dispatch({
            type: COMMON_WORKSPACES_SELECT_ACTIVE_WORKSPACE,
            workspaceId,
        });
    };

    const updateSnapshotDate = () => {
        const lastSnapshotDate = component.resources.SupportedDate;
        return {
            type: COMMON_WORKSPACES_UPDATE_SNAPSHOT_DATE,
            lastSnapshotDate: dayjs.utc(lastSnapshotDate),
        };
    };

    const selectActiveList = (opportunityListId: string) => {
        return (dispatch: ThunkDispatchCommon) => {
            const { listId } = proNavigator.getParams();
            if (listId !== opportunityListId) {
                proNavigator.updateParams({ listId: opportunityListId });
            }
            dispatch(selectLeadListAction(opportunityListId));
            // TODO: Keep this one until refactored
            return dispatch({
                type: COMMON_WORKSPACES_SELECT_ACTIVE_LIST,
                activeListId: opportunityListId,
            });
        };
    };

    const selectActiveSearchList =
        (searchQueryDefinitionId: string, rawQueryParams: FixmeType) => (dispatch: ThunkDispatchCommon) => {
            const MODE_CATEGORIES = "categories";
            const MODE_TECHNOLOGIES = "technologies";
            const FUNCTIONALITY_EXCLUDE = "exclude";
            const FUNCTIONALITY_INCLUDE = "include";

            const queryParams = _.cloneDeep(rawQueryParams);
            const { searchId, newLeadsOnly } = proNavigator.getParams();

            if (searchId !== searchQueryDefinitionId) {
                proNavigator.updateParams({
                    searchId: searchQueryDefinitionId,
                });
            }

            if (newLeadsOnly) {
                queryParams.filters.newLeadsOnly = safeParse(newLeadsOnly);
            }

            const { technographics_excluded, technographics_included } = queryParams.filters;

            if (technographics_excluded) {
                let mode;
                let values = [];
                const { technographics_parent_category, technographics_technology } = technographics_excluded;
                if (technographics_technology) {
                    values = technographics_technology.map((item: FixmeType) => ({ text: item }));
                }

                if (technographics_parent_category) {
                    values = technographics_parent_category.map((text: FixmeType) => ({ text }));
                    mode = MODE_CATEGORIES;
                }

                if (technographics_technology) {
                    values = technographics_technology.map((text: FixmeType) => ({ text }));
                    mode = MODE_TECHNOLOGIES;
                }

                queryParams.filters.technographics_excluded = {
                    functionality: FUNCTIONALITY_EXCLUDE,
                    mode,
                    values,
                };
            }

            if (technographics_included) {
                let mode;
                let values = [];
                const { technographics_parent_category, technographics_technology } = technographics_included;
                if (technographics_technology) {
                    values = technographics_technology.map((item: FixmeType) => ({ text: item }));
                }

                if (technographics_parent_category) {
                    values = technographics_parent_category.map((item: FixmeType) => ({ text: item }));
                    mode = MODE_CATEGORIES;
                }

                if (technographics_technology) {
                    values = technographics_technology.map((item: FixmeType) => ({ text: item }));
                    mode = MODE_TECHNOLOGIES;
                }

                queryParams.filters.technographics_included = {
                    functionality: FUNCTIONALITY_INCLUDE,
                    mode,
                    values,
                };
            }

            return dispatch({
                type: WORKSPACE_SALES_SELECT_ACTIVE_SAVED_SEARCH_LIST,
                searchId: searchQueryDefinitionId,
                queryParams,
            });
        };

    const unSelectActiveSearchList = () => {
        return (dispatch: ThunkDispatchCommon) => {
            proNavigator.updateParams({
                searchId: null,
            });

            return dispatch({ type: WORKSPACE_SALES_UNSELECT_ACTIVE_SAVED_SEARCH_LIST });
        };
    };

    const fetchWorkspaces = () => {
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            try {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_WORKSPACES,
                });
                const workspaces = await api.getWorkspaces();
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_WORKSPACES_SUCCESS,
                    workspaces,
                });
                return getState().commonWorkspace.workspaces;
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_WORKSPACES_FAILED,
                });
            }
        };
    };

    const fetchListOpportunities = (
        workspaceId: string,
        opportunitiesListId: string,
        params: any = {},
        holdRightBarOpen = false,
    ) => {
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            try {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_LIST_DATA_START,
                    holdRightBarOpen,
                });
                const { lastSnapshotDate, tableSearchTerm, tableOrderBy } = getState().commonWorkspace;
                const selectedSignalFilterId = selectActiveSignalFilterId(getState());
                const selectedSignalSubFilterId = selectActiveSignalSubFilterId(getState());
                const allCountriesSelected = selectActiveSignalsTab(getState()) !== 0;

                if (selectedSignalFilterId && !params.eventFilterType) {
                    params.eventFilterType = selectedSignalFilterId;
                }

                if (selectedSignalSubFilterId && !params.eventFilterSubType) {
                    params.eventFilterSubType = selectedSignalSubFilterId;
                }

                if (allCountriesSelected) {
                    params.allCountries = true;
                }

                if (tableSearchTerm) {
                    params.filter = `Site;contains;"${tableSearchTerm}"`;
                }
                if (tableOrderBy) {
                    params.orderBy = tableOrderBy;
                }
                const response = await api.getOpportunitiesList({
                    workspaceId,
                    opportunitiesListId,
                    ...params,
                    date: lastSnapshotDate.format("YYYY-MM"),
                });
                if (isListActive(opportunitiesListId, workspaceId)) {
                    const { TotalCount = 0, Data: Records = [], KeysDataVerification, Header, Filters } = response;
                    dispatch({
                        type: COMMON_WORKSPACES_SET_ACTIVE_LIST_DATA_SUCCESS,
                        data: {
                            TotalCount,
                            Records,
                            KeysDataVerification,
                            Header,
                            Filters,
                        },
                    });
                }
                return response;
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_WORKSPACES_FAILED,
                });
            }
        };
    };

    const setLoadingTable = () => ({ type: COMMON_WORKSPACES_FETCH_WORKSPACES });

    const removeOpportunities = (items: FixmeType) => {
        const itemsToRemove = Array.isArray(items) ? items : [items];
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            try {
                dispatch({
                    type: COMMON_WORKSPACES_OPTIMISTIC_REMOVE_OPPORTUNITIES,
                    itemsToRemove,
                });
                const { activeWorkspaceId, activeListId, workspaces } = getState().commonWorkspace;
                await api.deleteOpportunities(
                    activeWorkspaceId,
                    activeListId,
                    itemsToRemove.map(({ site }) => ({
                        Domain: site,
                    })),
                );
                const getActiveList = () => {
                    const lists = workspaces[0].opportunityLists;
                    return lists.find(({ opportunityListId }) => opportunityListId === activeListId);
                };
                const activeList = getActiveList();
                // @ts-ignore FIXME: strict migration - 'activeList' is possibly 'undefined' — list resolved from active state, assumed present at call time
                dispatch(fetchSignalsForAllOrInverseThunkAction(activeWorkspaceId, activeListId, activeList.country));
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_WORKSPACES_FAILED,
                });
                // @ts-ignore FIXME: strict migration - 'e' is of type 'unknown' — catch binding, legacy error logging assumes Error shape
                Sentry.error("error in removeOpportunities in common_worksapce_action_creators.ts:" + e.message, e);
            }
        };
    };

    const createWorkspace = () => {
        return async (dispatch: ThunkDispatchCommon) => {
            try {
                const workspace = await api.createWorkspace();
                if (workspace.workspaceId) {
                    dispatch({
                        type: COMMON_WORKSPACES_ADD_WORKSPACE,
                        workspace,
                    });
                }
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_WORKSPACES_FAILED,
                    workspaces: null,
                });
            }
        };
    };

    const createOpportunitiesList = (listName: string, workspaceId: string, opportunities: FixmeType) => {
        return async (dispatch: ThunkDispatchCommon) => {
            try {
                const { InitialCountry: country } = component.resources;
                const res = await api.createOpportunitiesList(workspaceId, listName, country, opportunities);
                const newList = {
                    ...res.opportunitiesList,
                };
                dispatch({
                    type: COMMON_WORKSPACES_ADD_OPPORTUNITY_LIST,
                    newList,
                    workspaceId,
                });
                return newList;
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_WORKSPACES_FAILED,
                });
            }
        };
    };

    const deleteOpportunityList = (workspaceId: string, listId: string) => async (dispatch: ThunkDispatchCommon) => {
        await api.deleteList(workspaceId, listId);

        dispatch(removeListIdFromSignalsUseThunkAction(listId));

        return dispatch({
            type: COMMON_WORKSPACES_DELETE_OPPORTUNITY_LIST,
            listId,
            workspaceId,
        });
    };

    const updateOpportunityList =
        (workspaceId: string, listId: string, { friendlyName, country }: FixmeType) =>
        async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            dispatch({ type: COMMON_WORKSPACES_UPDATE_LIST_INFO_START });
            await api.updateListInfo(workspaceId, listId, {
                friendlyName,
                country,
            });
            const res = dispatch({
                type: COMMON_WORKSPACES_UPDATE_LIST_INFO,
                friendlyName,
                country,
                listId,
                workspaceId,
            });
            const { isRightBarOpen } = getState().commonWorkspace;
            if (isRightBarOpen) {
                dispatch(getRightBarData());
            }
            return res;
        };

    const updateOpportunityListSettings =
        (workspaceId: string, opportunityListId: string, settings: FixmeType) =>
        async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            const res = await api.updateOpportunityListSettings({
                workspaceId,
                opportunityListId,
                settings,
            });
            const getActiveList = () => {
                const lists = getState().commonWorkspace.workspaces[0].opportunityLists;
                return lists.find(({ opportunityListId }) => opportunityListId === opportunityListId);
            };
            const activeList = getActiveList();

            // @ts-ignore FIXME: strict migration - 'activeList' is possibly 'undefined' — list resolved from active state, assumed present at call time
            dispatch(fetchSignalsForCountryThunkAction(workspaceId, opportunityListId, activeList.country));
            // @ts-ignore FIXME: strict migration - 'activeList' is possibly 'undefined' — list resolved from active state, assumed present at call time
            dispatch(fetchSignalsForAllOrInverseThunkAction(workspaceId, opportunityListId, activeList.country));

            // @ts-ignore FIXME: strict migration - 'activeList' is possibly 'undefined' — list resolved from active state, assumed present at call time
            if (!_.isEqual(settings.alerts, activeList.settings.alerts)) {
                dispatch(showSuccessToast(i18nFilter()("workspace.settings.update_countries.toast.success")));
            }
            dispatch({
                type: COMMON_WORKSPACES_UPDATE_LIST_SETTINGS,
                settings,
                opportunityListId,
                workspaceId,
            });
            const { isRightBarOpen } = getState().commonWorkspace;
            if (isRightBarOpen) {
                dispatch(getRightBarData());
            }
            return res;
        };

    const editOpportunityList = (
        workspaceId: string,
        list: IOpportunityListItem,
        editFeedGeos?: FixmeType,
        selectedTab?: FixmeType,
    ) => {
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            try {
                const { unsupportedFeatures } = getState().commonWorkspace;
                const {
                    action,
                    list: { opportunityListId, friendlyName, country, settings },
                } = await editListModal({
                    unsupportedFeatures,
                    list,
                    mode: "edit",
                    editFeedGeos,
                    selectedTab,
                    variant: "old",
                });
                let p1 = Promise.resolve();
                let p2 = Promise.resolve();
                switch (action) {
                    case "update":
                        if (friendlyName !== list.friendlyName || country !== list.country) {
                            // @ts-ignore FIXME: strict migration - Type 'Promise<{...}>' is not assignable to type 'Promise<void>' — dispatch returns the thunk's resolved action, p1 is seeded as Promise<void> and only aggregated via Promise.all, value discarded
                            p1 = dispatch(
                                updateOpportunityList(workspaceId, opportunityListId, {
                                    friendlyName,
                                    country,
                                }),
                            );
                        }
                        if (!_.isEqual(settings, list.settings)) {
                            p2 = dispatch(updateOpportunityListSettings(workspaceId, opportunityListId, settings));
                        }
                        return Promise.all([p1, p2]);

                    case "delete":
                        await dispatch(deleteOpportunityList(workspaceId, opportunityListId));
                        dispatch(selectActiveList(OVERVIEW_ID));
                        return;
                }
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_WORKSPACES_FAILED,
                });
            }
        };
    };

    const addOpportunities = (
        workspaceId: string,
        opportunitiesListId: string,
        opportunities: FixmeType,
        holdRightBarOpen = false,
        isFeatureSavedSearchesEnabled = false,
    ) => {
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            try {
                dispatch({
                    type: COMMON_WORKSPACES_ADD_OPPORTUNITIES_START,
                });

                const commonWorkspaceObj = getState().commonWorkspace;

                const { leadsGeneratorReport } = commonWorkspaceObj;
                const activeList = selectActiveOpportunityList(commonWorkspaceObj);

                const updatedList = await api.addOpportunities(
                    workspaceId,
                    opportunitiesListId,
                    opportunities,
                    leadsGeneratorReport?.queryId,
                    leadsGeneratorReport?.runId,
                );

                dispatch({
                    type: COMMON_WORKSPACES_ADD_OPPORTUNITIES_SUCCESS,
                    updatedList: {
                        ...updatedList,
                    },
                    workspaceId,
                });

                if (updatedList?.query) {
                    dispatch({
                        type: WORKSPACE_SALES_UPDATE_SAVED_SEARCH_LIST,
                        workspaceId,
                        queryDefinition: updatedList.query.queryDefinition,
                        lastRun: updatedList.query.lastRun,
                    });
                }

                let successText = "";
                switch (workspaceType) {
                    case "sales":
                        successText =
                            opportunities.length > 1
                                ? "workspace.sales.add_opportunities.multiple"
                                : "workspace.sales.add_opportunities";
                        break;
                    default:
                        successText =
                            opportunities.length > 1
                                ? "wa.ao.header.trackSuccess.multiple"
                                : "wa.ao.header.trackSuccess";
                        break;
                }

                if (updatedList.opportunities.length < opportunities.length) {
                    successText = "workspace.add_opportunities.duplicates_error";
                }

                if (workspaceType === "sales" && isFeatureSavedSearchesEnabled) {
                    dispatch(
                        showSuccessToast(
                            getToastItemComponent({
                                text: i18nFilter()(successText),
                                linkText: i18nFilter()("workspace.add_opportunities.success.see_list"),
                                onClick: () => {
                                    dispatch(toggleWebsitesWizard(false));
                                    dispatch(selectActiveList(opportunitiesListId));
                                    dispatch(fetchListOpportunities(workspaceId, opportunitiesListId, {}, false));
                                    dispatch(fetchRecommendations(workspaceId, opportunitiesListId));
                                },
                            }),
                        ),
                    );
                } else {
                    dispatch(showSuccessToast(i18nFilter()(successText)));
                }

                if (isListActive(opportunitiesListId, workspaceId)) {
                    // @ts-ignore FIXME: strict migration - 'activeList' is possibly 'undefined' — active list resolved from state, assumed present when list is active
                    if (!activeList.opportunities.length) {
                        dispatch(fetchRecommendations(workspaceId, opportunitiesListId));
                    }
                }

                dispatch(fetchListOpportunities(workspaceId, opportunitiesListId, {}, holdRightBarOpen));

                return updatedList;
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_ADD_OPPORTUNITIES_FAIL,
                });
                dispatch(showErrorToast(i18nFilter()("workspace.add_opportunities.quota_error")));
                allTrackers.dispatchTrackEvent("Pop Up", "show", "error/quota limit reached");
            }
        };
    };

    const subscribeEmailDigest = (workspaceId: string, opportunitiesListId: string) => {
        return async (dispatch: ThunkDispatchCommon) => {
            try {
                await api.subscribeEmailDigest(workspaceId, opportunitiesListId);

                dispatch({
                    type: WORKSPACE_SALES_MANIPULATION_EMAIL_DIGEST,
                    isSubscriptionActive: true,
                    workspaceId,
                    opportunitiesListId,
                });

                dispatch(showSuccessToast(i18nFilter()("workspace.sales.email.digest.toast.subscribe")));
            } catch (e) {
                dispatch(showErrorToast(i18nFilter()("workspace.sales.email.digest.toast.subscribe.error")));

                dispatch({
                    type: WORKSPACE_SALES_MANIPULATION_EMAIL_DIGEST,
                    isSubscriptionActive: false,
                    workspaceId,
                    opportunitiesListId,
                });
            }
        };
    };

    const unSubscribeEmailDigest = (workspaceId: string, opportunitiesListId: string) => {
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            try {
                await api.unSubscribeEmailDigest(workspaceId, opportunitiesListId);

                dispatch({
                    type: WORKSPACE_SALES_MANIPULATION_EMAIL_DIGEST,
                    isSubscriptionActive: false,
                    workspaceId,
                    opportunitiesListId,
                });

                dispatch(showSuccessToast(i18nFilter()("workspace.sales.email.digest.toast.unsubscribe")));
            } catch (e) {
                dispatch(showErrorToast(i18nFilter()("workspace.sales.email.digest.toast.unsubscribe.error")));

                dispatch({
                    type: WORKSPACE_SALES_MANIPULATION_EMAIL_DIGEST,
                    isSubscriptionActive: true,
                    workspaceId,
                    opportunitiesListId,
                });
            }
        };
    };

    const fetchRecommendations = (workspaceId: string, listId: string) => {
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            try {
                dispatch({
                    type: COMMON_WORKSPACES_FETCH_RECOMMENDATIONS,
                });
                const { lastSnapshotDate } = getState().commonWorkspace;
                const recommendations = await api.getRecommendations(
                    workspaceId,
                    listId,
                    lastSnapshotDate.format("YYYY-MM"),
                );
                if (isListActive(listId, workspaceId)) {
                    dispatch({
                        type: COMMON_WORKSPACES_UPDATE_RECOMMENDATIONS,
                        recommendations: recommendations.map((item: FixmeType) => ({
                            ...item,
                            TopCountryName: countryTextByIdFilter()(item.TopCountry),
                            Date: lastSnapshotDate.valueOf(),
                        })),
                    });
                }
            } catch (e) {
                dispatch(showErrorToast(i18nFilter()("workspace.recommendation_sidebar.fetch.fail")));
                dispatch({
                    type: COMMON_WORKSPACES_UPDATE_RECOMMENDATIONS,
                    recommendations: [],
                });
            }
        };
    };

    const dismissRecommendation = (workspaceId: string, listId: string, recommendation: FixmeType) => {
        return async (dispatch: ThunkDispatchCommon) => {
            try {
                dispatch(removeRecommendations([{ Domain: recommendation }]));
                await api.dismissRecommendation(workspaceId, listId, recommendation);
            } catch (err) {
                dispatch(showErrorToast(i18nFilter()("workspace.recommendation_sidebar.dismiss.fail")));
            }
        };
    };

    const addRecommendations = (
        workspaceId: string,
        opportunitiesListId: string,
        recommendations: FixmeType,
        holdRightBarOpen = false,
    ) => {
        return (dispatch: ThunkDispatchCommon) => {
            dispatch(removeRecommendations(recommendations));
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(
                        dispatch(addOpportunities(workspaceId, opportunitiesListId, recommendations, holdRightBarOpen)),
                    );
                }, 1000);
            });
        };
    };

    const removeRecommendations = (recommendations: FixmeType) => {
        return (dispatch: ThunkDispatchCommon) => {
            dispatch({
                type: COMMON_WORKSPACES_REMOVE_RECOMMENDATIONS,
                recommendations,
            });
        };
    };

    const toggleRightBar = (isRightBarOpen: boolean) => {
        return (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            const { selectedDomain, selectedTab } = getState().commonWorkspace;
            if (selectedDomain && selectedDomain.domain) {
                allTrackers.trackEvent(
                    "Expanded Side bar",
                    isRightBarOpen ? "open" : "close",
                    `${selectedDomain.domain}/Tab/${selectedTab.toLowerCase().replace("_", " ")}`,
                );
            }
            dispatch({
                type: COMMON_WORKSPACES_TOGGLE_RIGHTBAR,
                isRightBarOpen,
            });
        };
    };

    const toggleRecommendations = (isOpen: boolean) => ({
        type: COMMON_WORKSPACES_TOGGLE_RECOMMENDATIONS,
        isOpen,
    });

    const toggleWebsitesWizard = (isOpen: boolean) => ({
        type: WORKSPACE_SALES_MANIPULATION_WEBSITES_WIZARD,
        isOpen,
    });

    const toggleWebsitesModal = () => ({
        type: "COMMON_WORKSPACES_MANIPULATION_WEBSITES_MODAL",
    });

    const toggleUnlockModal = (isOpen: boolean) => ({
        type: "COMMON_WORKSPACES_MANIPULATION_UNLOCK_MODAL",
    });

    const getRightBarData = () => {
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            const { commonWorkspace }: { commonWorkspace: ICommonWorkspaceState } = getState();
            const { unsupportedFeatures, activeListId, activeWorkspaceId, lastSnapshotDate, selectedDomain } =
                commonWorkspace;
            // @ts-ignore FIXME: strict migration - 'selectActiveOpportunityList' possibly returns undefined — active list assumed present when right bar opens
            const { country } = selectActiveOpportunityList(commonWorkspace);
            if (selectedDomain) {
                const { domain } = selectedDomain;
                if (domain) {
                    dispatch(
                        getDescription({
                            country,
                            domain,
                            lastSnapshotDate,
                        }),
                    );
                    dispatch(
                        getRanksData({
                            country,
                            domain,
                            lastSnapshotDate,
                        }),
                    );
                    dispatch(
                        getEnrichedData({
                            opportunityListId: activeListId,
                            workspaceId: activeWorkspaceId,
                            country,
                            domain,
                            lastSnapshotDate,
                        }),
                    );
                    if (!unsupportedFeatures.has(LIST_SETTING_FEED)) {
                        dispatch(fetchFeedsThunk(activeWorkspaceId, activeListId, domain));
                        dispatch(fetchTopCountriesThunk(domain));
                        dispatch(fetchAdNetworksThunkAction(domain, country));
                        dispatch(fetchSiteInfoThunkAction(domain, country));
                    }
                }
            }
        };
    };

    const getDescription = ({ country, domain, lastSnapshotDate }: FixmeType) => {
        return async (dispatch: ThunkDispatchCommon) => {
            const startDate = lastSnapshotDate.format("YYYY|MM|DD");
            const endDate = lastSnapshotDate.endOf("month").format("YYYY|MM|DD");
            dispatch({
                type: COMMON_WORKSPACES_FETCH_RANKS_DATA,
            });
            try {
                const result = await api.getWebsiteHeader(domain, country, startDate, endDate);
                dispatch({
                    type: COMMON_WORKSPACES_SET_DESCRIPTION_DATA,
                    description: getSiteDataFromResponse(result, domain)?.description ?? "",
                });
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_SET_DESCRIPTION_DATA,
                    description: "",
                });
            }
        };
    };

    const getRanksData = ({ country, domain, lastSnapshotDate }: FixmeType) => {
        return async (dispatch: ThunkDispatchCommon) => {
            const startDate = lastSnapshotDate.format("YYYY|MM|DD");
            const endDate = lastSnapshotDate.endOf("month").format("YYYY|MM|DD");
            dispatch({
                type: COMMON_WORKSPACES_FETCH_RANKS_DATA,
            });
            try {
                const result = await api.getWebsiteRanks(domain, country, startDate, endDate);
                dispatch({
                    type: COMMON_WORKSPACES_SET_RANKS_DATA,
                    data: result.Data[domain],
                });
            } catch (e) {
                dispatch({
                    type: COMMON_WORKSPACES_SET_RANKS_DATA,
                    data: {},
                });
            }
        };
    };

    const getEnrichedData = ({ opportunityListId, workspaceId, country, domain, lastSnapshotDate }: FixmeType) => {
        return async (dispatch: ThunkDispatchCommon) => {
            const to = lastSnapshotDate.format("YYYY|MM|DD");

            // TODO: Use `WebAllowedDuration`
            const months = swSettings.components.Home.resources.IsTrial ? 5 : 23;

            const from = lastSnapshotDate.clone().subtract(months, "months").format("YYYY|MM|DD");
            dispatch({
                type: COMMON_WORKSPACES_FETCH_ENRICHED_DATA,
            });
            const data = await api.getEnrichedTableRow({
                opportunityListId,
                workspaceId,
                country,
                domain,
                from,
                to,
            });
            dispatch({
                type: COMMON_WORKSPACES_SET_ENRICHED_DATA,
                data,
            });
        };
    };

    const searchTableList = (searchTerm: string) => {
        return {
            type: COMMON_WORKSPACE_LIST_SEARCH,
            searchTerm,
        };
    };

    const sortTableList = (orderBy: string) => {
        return {
            type: COMMON_WORKSPACE_LIST_SORT,
            orderBy,
        };
    };

    const onRowSelected = (
        e: FixmeType,
        { site, favicon, company_name, site_category, medium_image, large_image, number_of_unseen_alerts }: FixmeType,
    ) => {
        return (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            const { salesWorkspace, commonWorkspace } = getState();
            const { selectedDomain, isRightBarOpen, isRecommendationOpen } = commonWorkspace;
            const { isRightBarOpen: isRightBarOpenSales } = salesWorkspace.common;

            if (!selectedDomain || isRecommendationOpen || selectedDomain.domain !== site) {
                const selectedTab = number_of_unseen_alerts > 0 ? FEED_TAB : ANALYSIS_TAB;
                if (!isRightBarOpen) {
                    allTrackers.trackEvent(
                        "Expanded Side bar",
                        "open",
                        `${site}/Tab/${selectedTab.toLowerCase().replace("_", " ")}`,
                    );
                }
                dispatch({
                    type: COMMON_WORKSPACES_SELECT_DOMAIN,
                    site,
                    favicon,
                    company_name,
                    site_category,
                    medium_image,
                    large_image,
                    number_of_unseen_alerts,
                    selectedTab,
                });
                // New action for selecting a website
                dispatch(
                    selectWebsiteAction({
                        domain: site,
                        favicon,
                    }),
                );
                dispatch(getRightBarData());
                /**
                 * Action toggle right bar only for sales workspace
                 */
                if (workspaceType === "sales") {
                    dispatch(toggleRightBarSales(true));
                }
            } else {
                /**
                 * Action toggle right bar only for sales workspace
                 */

                if (workspaceType === "sales") {
                    dispatch(toggleRightBarSales(!isRightBarOpenSales));
                } else {
                    dispatch(toggleRightBar(!isRightBarOpen));
                }
            }
        };
    };

    const setFeedItemFeedback = (feedItemsId: FixmeType, feedbackType: FixmeType, feedbackText: FixmeType) => {
        return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
            const { activeWorkspaceId, activeListId, selectedDomain } = getState().commonWorkspace;
            if (selectedDomain) {
                const { domain } = selectedDomain;
                if (domain) {
                    await api.setFeedItemFeedback(
                        activeWorkspaceId,
                        activeListId,
                        domain,
                        feedItemsId,
                        feedbackType,
                        feedbackText,
                    );
                    dispatch({
                        type: COMMON_WORKSPACES_SET_FEED_ITEM_FEEDBACK,
                        feedItemsId,
                        feedbackType,
                        feedbackText,
                    });
                    dispatch(showSuccessToast(i18nFilter()("workspace.feed_sidebar.feedback.toast.success")));
                }
            }
        };
    };

    const setUnsupportedFeatures = (unsupportedFeatures: FixmeType) => ({
        type: COMMON_WORKSPACES_SET_UNSUPPORTED_FEATURES,
        unsupportedFeatures,
    });

    const reset = () => ({
        type: COMMON_WORKSPACES_RESET,
    });

    const unsubscribeFromMonthlyDigest = () => async (dispatch: ThunkDispatchCommon) => {
        try {
            await api.unsubscribeFromMonthlyDigest();

            dispatch(showSuccessToast(i18nFilter()("workspace.sales.email.digest.toast.unsubscribe")));
        } catch (e) {
            dispatch(showErrorToast(i18nFilter()("workspaces.sales.digest.unsubscribe_error")));
        }
    };

    return {
        toggleUnlockModal,
        toggleWebsitesModal,
        selectActiveWorkspace,
        updateSnapshotDate,
        selectActiveList,
        selectActiveSearchList,
        unSelectActiveSearchList,
        fetchWorkspaces,
        fetchListOpportunities,
        setLoadingTable,
        removeOpportunities,
        createWorkspace,
        createOpportunitiesList,
        deleteOpportunityList,
        updateOpportunityList,
        editOpportunityList,
        addOpportunities,
        fetchRecommendations,
        dismissRecommendation,
        addRecommendations,
        setFeedItemFeedback,
        toggleWebsitesWizard,
        toggleRightBar,
        toggleRecommendations,
        onRowSelected,
        setUnsupportedFeatures,
        searchTableList,
        sortTableList,
        reset,
        subscribeEmailDigest,
        unSubscribeEmailDigest,
        unsubscribeFromMonthlyDigest,
    };
};

export const addSiteToWatchList = (domain: string) => {
    return async (dispatch: ThunkDispatchCommon) => {
        try {
            const { worksapceType, workspaceId, opportunityListId } = await addToWatchlistModal(domain);
            try {
                const api =
                    worksapceType === INVESTORS_TYPE
                        ? new InvestorsWorkspaceApiService()
                        : new SalesWorkspaceApiService();
                const res = await dispatch(
                    commonActionCreators({
                        api,
                        component: null,
                    }).addOpportunities(workspaceId, opportunityListId, [
                        {
                            Domain: domain,
                        },
                    ]),
                );
                return res;
            } catch (e) {
                return null;
            }
        } catch (e) {
            return null;
        }
    };
};
